export type HomeCheckInPartnerRecord = {
  id: number
  position?: number | null
  checkin_active?: boolean
  finished?: boolean
  reactions?: { like?: number }
}

export const isActiveCheckInPartner = (
  partner: HomeCheckInPartnerRecord
) => partner.checkin_active === true && partner.finished !== true

export const filterActiveCheckInPartners = <T extends HomeCheckInPartnerRecord>(
  partners: T[]
) => partners.filter(isActiveCheckInPartner)

export const selectFeaturedCheckInPartner = <T extends HomeCheckInPartnerRecord>(
  activePartners: T[],
  topLikeIds: number[]
): T | null => {
  for (const id of topLikeIds) {
    const partner = activePartners.find(entry => entry.id === id)
    if (partner) return partner
  }

  if (activePartners.length === 0) return null

  return [...activePartners].sort(
    (a, b) => (b.reactions?.like ?? 0) - (a.reactions?.like ?? 0)
  )[0]
}

export const selectRotatingCheckInPartners = <T extends HomeCheckInPartnerRecord>(
  activePartners: T[],
  featured: T | null,
  topLikeIds: number[]
): T[] => {
  const rotating = featured
    ? activePartners.filter(partner => partner.id !== featured.id)
    : activePartners

  const orderMap = new Map(topLikeIds.map((id, index) => [id, index]))

  return [...rotating].sort((a, b) => {
    const left = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER
    const right = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER

    if (left !== right) return left - right

    return (a.position ?? 0) - (b.position ?? 0)
  })
}

/** Ordena parceiros para preview compacto na home: curtidas, top_likes, posição. */
export const sortPartnersForHomePreview = <T extends HomeCheckInPartnerRecord>(
  partners: T[],
  topLikeIds: number[]
): T[] => {
  const topLikeOrder = new Map(topLikeIds.map((id, index) => [id, index]))

  return [...partners].sort((a, b) => {
    const likeDiff = (b.reactions?.like ?? 0) - (a.reactions?.like ?? 0)
    if (likeDiff !== 0) return likeDiff

    const left = topLikeOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER
    const right = topLikeOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER
    if (left !== right) return left - right

    return (a.position ?? 0) - (b.position ?? 0)
  })
}

export const orderPartnersForHomePreview = sortPartnersForHomePreview

export const selectFeaturedPartnerId = <T extends HomeCheckInPartnerRecord>(
  partners: T[],
  topLikeIds: number[]
): number | null => {
  const sorted = sortPartnersForHomePreview(partners, topLikeIds)
  const top = sorted[0]
  if (!top) return null

  if ((top.reactions?.like ?? 0) > 0) return top.id

  for (const id of topLikeIds) {
    if (partners.some(partner => partner.id === id)) return id
  }

  return null
}

export const pickRotatingHomePartners = <T extends HomeCheckInPartnerRecord>(
  rotatingPartners: T[],
  slots: number,
  offset: number
): T[] => {
  if (slots <= 0 || rotatingPartners.length === 0) return []

  if (rotatingPartners.length <= slots) return rotatingPartners

  const normalizedOffset =
    ((offset % rotatingPartners.length) + rotatingPartners.length) %
    rotatingPartners.length

  return Array.from(
    { length: slots },
    (_, index) => rotatingPartners[(normalizedOffset + index) % rotatingPartners.length]
  )
}

/** Top curtidas fixo no primeiro slot; demais vagas rotacionam no pool restante. */
export const buildHomePartnersPreview = <T extends HomeCheckInPartnerRecord>(
  partners: T[],
  topLikeIds: number[],
  options: { limit: number; rotateOffset?: number }
): { featuredId: number | null; preview: T[] } => {
  const sorted = sortPartnersForHomePreview(partners, topLikeIds)
  const featuredId = selectFeaturedPartnerId(partners, topLikeIds)

  if (sorted.length === 0) return { featuredId: null, preview: [] }

  const featured =
    featuredId != null
      ? (sorted.find(partner => partner.id === featuredId) ?? sorted[0])
      : null

  const rotatingPool = featured
    ? sorted.filter(partner => partner.id !== featured.id)
    : sorted

  const rotatingSlots = featured ? Math.max(0, options.limit - 1) : options.limit
  const rotating = pickRotatingHomePartners(
    rotatingPool,
    rotatingSlots,
    options.rotateOffset ?? 0
  )

  const preview = featured
    ? [featured, ...rotating].slice(0, options.limit)
    : rotating.slice(0, options.limit)

  return {
    featuredId: featured?.id ?? null,
    preview
  }
}
